import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, ScanLine, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Modal, Button } from './ui';
import './SwitchUser.css';

/**
 * «Сменить сотрудника» — пересменка на одном ПК или ТСД одним сканом бейджа.
 *
 * Раньше, чтобы передать место другому, надо было выйти, попасть на форму логина и
 * найти там «Вход по бейджу» — и на складе проще было работать дальше под тем, кто
 * вошёл первым (или под общей учёткой admin). Теперь поле скана открывается сразу;
 * прежняя сессия закрывается только после успешного скана, промах её не трогает.
 * Сканер ТСД кладёт код в активное поле, поэтому поле всегда в фокусе.
 */
export function SwitchUserDialog({ open, onClose }) {
  const { loginByBadge, user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [greeting, setGreeting] = useState('');

  const focus = useCallback(() => inputRef.current?.focus(), []);
  useEffect(() => {
    if (!open) return undefined;
    setCode(''); setError(''); setGreeting('');
    const t0 = setTimeout(focus, 30);
    const t = setInterval(focus, 1500);   // кликнули мимо — следующий скан не должен уйти в страницу
    return () => { clearTimeout(t0); clearInterval(t); };
  }, [open, focus]);

  const submit = useCallback(async (value) => {
    const scanned = (value || '').trim();
    if (!scanned || busy) return;
    setBusy(true);
    setError('');
    try {
      const u = await loginByBadge(scanned);
      setGreeting(u.name);
      setTimeout(() => { onClose?.(); navigate('/'); }, 700);
    } catch (e) {
      setError(e.response?.data?.detail || e.friendlyMessage || 'Не удалось войти по бейджу');
      setCode('');
      focus();
    } finally {
      setBusy(false);
    }
  }, [busy, loginByBadge, navigate, onClose, focus]);

  // Сканер ТСД: код приходит событием. Перехватываем раньше экрана под окном
  // (capture), иначе экран сканирования принял бы бейдж за ШК товара.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      const c = (e.detail?.code || '').trim();
      if (!c) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      submit(c);
    };
    window.addEventListener('tsd-scan', h, { capture: true });
    return () => window.removeEventListener('tsd-scan', h, { capture: true });
  }, [open, submit]);

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Сменить сотрудника" size="sm"
           className="switch-user">
      <div className="switch-user__body">
        <ScanLine size={44} className="switch-user__icon" />
        <div className="switch-user__sub">
          Отсканируйте бейдж того, кто будет работать дальше.
          {user?.name && <> Сейчас вошёл: <b>{user.name}</b>.</>}
        </div>
        <input
          ref={inputRef}
          className="form-input switch-user__input"
          value={code}
          disabled={busy}
          autoComplete="off"
          aria-label="Код бейджа"
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(code); }}
          placeholder="Отсканируйте бейдж"
        />
        {greeting && (
          <div className="switch-user__msg switch-user__msg--ok">
            <CheckCircle2 size={18} /> Здравствуйте, {greeting}!
          </div>
        )}
        {error && (
          <div className="switch-user__msg switch-user__msg--err" role="alert">
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        <div className="switch-user__foot">
          Бейдж работает только в вашу смену. Предыдущий сотрудник выйдет после успешного скана.
        </div>
        <div className="switch-user__actions">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Отмена</Button>
        </div>
      </div>
    </Modal>
  );
}

/** Кнопка в меню (ПК) и в верхней панели (телефон, ТСД). Только сотрудникам склада. */
export default function SwitchUserButton({ variant = 'sidebar', collapsed = false }) {
  const { isInternalStaff } = useAuth();
  const [open, setOpen] = useState(false);
  if (!isInternalStaff) return null;
  const cls = variant === 'topbar' ? 'mobile-topbar__btn' : 'nav-item switch-user-btn';
  return (
    <>
      <button type="button" className={cls} onClick={() => setOpen(true)}
              title="Сменить сотрудника — отсканировать бейдж" aria-label="Сменить сотрудника">
        <UserCog size={variant === 'topbar' ? 21 : 16} />
        {variant === 'sidebar' && !collapsed && <span>Сменить сотрудника</span>}
      </button>
      <SwitchUserDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
